/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */
import { nonNull } from '../utils/util';
import { InputState, InputStateConfig, NORMAL_VALUE_KEY, UnknownInputValue } from './general.model';

export type PupilsEnteredState = PupilSymptoms & {
  readonly inputState: InputState.ENTERED;
};

export type PupilsValue = UnknownInputValue | PupilsEnteredState;

export type PupilSymptoms = {
  readonly size?: PupilsSymptomValue;
  readonly reactionToLight?: PupilsSymptomValue;
  readonly shape?: PupilsSymptomValue;
};

export interface PupilsSymptomValue {
  readonly left?: string;
  readonly right?: string;
}

export function isNormalPupils(value: PupilsValue | null) {
  return (
    value !== null &&
    value.inputState === InputState.ENTERED &&
    isPupilCategoryNormal(value?.size) &&
    isPupilCategoryNormal(value?.reactionToLight) &&
    isPupilCategoryNormal(value?.shape)
  );
}

export const PUPILS_NORMAL_STATE: PupilsValue = {
  inputState: InputState.ENTERED,
  size: { left: NORMAL_VALUE_KEY, right: NORMAL_VALUE_KEY },
  reactionToLight: { left: NORMAL_VALUE_KEY, right: NORMAL_VALUE_KEY },
  shape: { right: NORMAL_VALUE_KEY, left: NORMAL_VALUE_KEY },
};

function isPupilCategoryNormal(value: PupilsSymptomValue | undefined) {
  return nonNull(value) && value.right === NORMAL_VALUE_KEY && value.left === NORMAL_VALUE_KEY;
}

/* Returns true if pupil symptom for given side is defined and not normal (= abnormal) */
export function isPupilAbnormal(value: PupilSymptoms | null, keyLeftRight: keyof PupilsSymptomValue) {
  return (
    isPupilSymptomAbnormal(value?.size, keyLeftRight) ||
    isPupilSymptomAbnormal(value?.reactionToLight, keyLeftRight) ||
    isPupilSymptomAbnormal(value?.shape, keyLeftRight)
  );
}

/* Returns true if pupil symptom for given side is defined and not normal (= abnormal) */
export function isPupilSymptomAbnormal(value: PupilsSymptomValue | undefined, keyLeftRight: keyof PupilsSymptomValue) {
  const symptom = nonNull(value) ? value[keyLeftRight] : value;
  return nonNull(symptom) && symptom !== NORMAL_VALUE_KEY;
}

export type PupilsConfig = {
  readonly size: PupilsConfigOption;
  readonly reactionToLight: PupilsConfigOption;
  readonly shape: PupilsConfigOption;
} & InputStateConfig;

export interface PupilsConfigOption {
  readonly label: string;
  readonly signs: readonly { readonly key: string; readonly label: string }[];
}

export type PupilsType = 'pupils';
